import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  ChevronLeft, LogOut, LayoutDashboard, ShieldAlert, Activity, Search,
  HeartPulse, RefreshCw, Download, Globe, Clock, Server, Layers,
  AlertTriangle, Hash, ArrowUpDown
} from 'lucide-react';
import { Banner } from '../components/Section';
import {
  getAdminStats, getAdminScans, getHealth,
  type AdminStats, type AdminScan, type ScanQuery
} from '../lib/api';
import {
  StatCard, Columns, AreaLine, HBars, Donut, SeverityBar, Heatmap,
  ChartEmpty, scoreColor, scoreTextClass, POSTURE_COLOR, POSTURE_ORDER
} from '../components/admin/charts';

type View = 'overview' | 'security' | 'traffic' | 'explorer' | 'health';

const NAV: { id: View; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'overview',  label: 'Overview',  icon: LayoutDashboard },
  { id: 'security',  label: 'Security',  icon: ShieldAlert },
  { id: 'traffic',   label: 'Traffic',   icon: Activity },
  { id: 'explorer',  label: 'Explorer',  icon: Search },
  { id: 'health',    label: 'Health',    icon: HeartPulse }
];

export function AdminPage() {
  const [view, setView] = useState<View>('overview');

  const stats = useQuery({
    queryKey: ['admin-stats'],
    queryFn: getAdminStats,
    refetchInterval: 60_000
  });

  const title = NAV.find(n => n.id === view)?.label ?? 'Admin';

  return (
    <div className="app-bg flex min-h-screen flex-col lg:h-screen lg:flex-row lg:overflow-hidden">
      <Sidebar view={view} setView={setView} email={stats.data?.admin_email} />

      <div className="flex min-w-0 flex-1 flex-col lg:overflow-hidden">
        <Topbar
          title={title}
          generatedAt={stats.data?.generated_at}
          fetching={stats.isFetching}
          onRefresh={() => stats.refetch()}
        />

        {/* Mobile section switcher */}
        <nav className="flex gap-1.5 overflow-x-auto border-b border-line bg-panel/70 px-3 py-2 lg:hidden">
          {NAV.map(n => (
            <button
              key={n.id}
              onClick={() => setView(n.id)}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                view === n.id ? 'bg-sentinel-600 text-white' : 'bg-panel-2 text-ink-2'
              }`}
            >
              <n.icon className="h-3.5 w-3.5" aria-hidden="true" />
              {n.label}
            </button>
          ))}
        </nav>

        <main className="mx-auto w-full max-w-[1400px] flex-1 space-y-4 px-3 py-5 sm:px-6 sm:py-6 lg:overflow-y-auto lg:px-8">
          {stats.isError && view !== 'explorer' && view !== 'health' && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              {(stats.error as Error)?.message ?? 'Failed to load stats.'}
            </div>
          )}

          {view === 'overview' && stats.data && <OverviewView s={stats.data} />}
          {view === 'security' && stats.data && <SecurityView s={stats.data} />}
          {view === 'traffic'  && stats.data && <TrafficView s={stats.data} />}
          {view === 'explorer' && <ExplorerView />}
          {view === 'health'   && <HealthView s={stats.data} />}

          {!stats.data && !stats.isError && view !== 'explorer' && view !== 'health' && <LoadingSkeleton />}
        </main>
      </div>
    </div>
  );
}

// ===========================================================================
// Shell
// ===========================================================================
function Sidebar({ view, setView, email }: { view: View; setView: (v: View) => void; email?: string }) {
  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-panel/80 backdrop-blur-sm lg:flex">
      <Link to="/" className="flex items-center gap-2 border-b border-line px-5 py-4">
        <img src="/logo.png" alt="PROTBYTE Sentinel" width={26} height={26} draggable={false} />
        <span className="font-display text-[15px] font-bold tracking-tight text-ink">
          PROTBYTE <span className="text-accent">Sentinel</span>
        </span>
      </Link>

      <div className="px-3 pt-4">
        <p className="px-2 pb-2 text-[10px] font-bold uppercase tracking-wider text-ink-3">Console</p>
        <nav className="space-y-0.5">
          {NAV.map(n => {
            const active = view === n.id;
            return (
              <button
                key={n.id}
                onClick={() => setView(n.id)}
                className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-sentinel-50 text-accent'
                    : 'text-ink-2 hover:bg-panel-2 hover:text-ink'
                }`}
              >
                <n.icon className="h-4 w-4" strokeWidth={2.1} aria-hidden="true" />
                {n.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="mt-auto border-t border-line p-3">
        {email && (
          <div className="mb-2 rounded-md bg-panel-2 px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">Signed in</p>
            <p className="truncate text-xs font-medium text-ink-2" title={email}>{email}</p>
          </div>
        )}
        <Link to="/" className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-ink-2 hover:bg-panel-2">
          <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Back to app
        </Link>
        <a
          href="/cdn-cgi/access/logout"
          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-ink-2 hover:bg-panel-2"
          title="Sign out of Cloudflare Access"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
        </a>
      </div>
    </aside>
  );
}

function Topbar({ title, generatedAt, fetching, onRefresh }: {
  title: string; generatedAt?: number; fetching: boolean; onRefresh: () => void;
}) {
  return (
    <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-panel/85 px-3 py-3 backdrop-blur-sm sm:px-6 lg:px-8">
      <Link to="/" className="flex items-center gap-2 lg:hidden">
        <img src="/logo.png" alt="PROTBYTE Sentinel" width={24} height={24} draggable={false} />
      </Link>
      <div className="min-w-0">
        <h1 className="font-display text-lg font-bold tracking-tight text-ink">{title}</h1>
        <p className="hidden text-[11px] text-ink-3 sm:block">
          {generatedAt ? `Updated ${relativeTime(generatedAt)}` : 'Loading…'}
        </p>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <span className="hidden items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 sm:inline-flex">
          <span className="ok-dot" aria-hidden="true" /> Live
        </span>
        <button
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-sm font-medium text-ink-2 hover:bg-panel-2"
          aria-label="Refresh data"
        >
          <RefreshCw className={`h-4 w-4 ${fetching ? 'animate-spin' : ''}`} aria-hidden="true" />
          <span className="hidden sm:inline">Refresh</span>
        </button>
        <a
          href="/cdn-cgi/access/logout"
          className="inline-flex items-center gap-1.5 rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-sm font-medium text-ink-2 hover:bg-panel-2 lg:hidden"
          aria-label="Sign out"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    </header>
  );
}

// ===========================================================================
// Section: Overview
// ===========================================================================
function OverviewView({ s }: { s: AdminStats }) {
  const last30 = useMemo(() => fillDaily(s.daily), [s.daily]);
  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Scans today" value={s.totals.day.toLocaleString()} delta={delta(s.totals.day, s.prev.day)} icon={<Activity className="h-4 w-4" />} />
        <StatCard label="Last 7 days" value={s.totals.week.toLocaleString()} delta={delta(s.totals.week, s.prev.week)} icon={<Activity className="h-4 w-4" />} />
        <StatCard label="Last 30 days" value={s.totals.month.toLocaleString()} delta={delta(s.totals.month, s.prev.month)} icon={<Activity className="h-4 w-4" />} />
        <StatCard label="All time" value={s.totals.all_time.toLocaleString()} hint="total scans" tone="neutral" icon={<Hash className="h-4 w-4" />} />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Unique domains" value={s.summary.unique_domains.toLocaleString()} tone="neutral" hint="distinct targets" icon={<Globe className="h-4 w-4" />} />
        <StatCard label="Avg score" value={s.summary.avg_score != null ? s.summary.avg_score.toFixed(1) : '—'} unit="/10" tone={scoreTone(s.summary.avg_score)} hint="all-time mean" icon={<ShieldAlert className="h-4 w-4" />} />
        <StatCard label="Avg scan time" value={s.summary.avg_duration_ms != null ? fmtDuration(s.summary.avg_duration_ms) : '—'} tone="neutral" hint="per scan" icon={<Clock className="h-4 w-4" />} />
        <StatCard label="Error rate" value={`${(s.summary.error_rate * 100).toFixed(1)}%`} tone={s.summary.error_rate > 0.05 ? 'bad' : s.summary.error_rate > 0 ? 'warn' : 'ok'} hint={`${s.summary.errors} failed`} icon={<AlertTriangle className="h-4 w-4" />} />
      </div>

      <Card title="Daily scan volume" subtitle="Last 30 days" accent="blue">
        <AreaLine points={last30.map(d => ({ key: d.d, value: d.c, tip: `${d.d}: ${d.c} scan${d.c === 1 ? '' : 's'}` }))} label="Daily scan volume" />
        <DailyFooter days={last30} />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Security posture" subtitle="Distribution of overall verdicts" accent="green">
          <PostureDonut s={s} />
        </Card>
        <Card title="Score distribution" subtitle="Overall score, bucketed 0–10" accent="amber">
          <ScoreHistogram s={s} />
        </Card>
      </div>
    </>
  );
}

// ===========================================================================
// Section: Security
// ===========================================================================
function SecurityView({ s }: { s: AdminStats }) {
  const moduleData = [
    { label: 'DNS',   value: s.module_avg.dns ?? 0 },
    { label: 'Email', value: s.module_avg.email ?? 0 },
    { label: 'Web',   value: s.module_avg.web ?? 0 },
    { label: 'SSL',   value: s.module_avg.ssl ?? 0 },
    { label: 'WHOIS', value: s.module_avg.whois ?? 0 }
  ].filter(m => m.value > 0);

  const gradeData = ['A', 'B', 'C', 'D', 'F'].map(g => ({
    label: g, value: s.grade_dist.find(x => x.grade === g)?.c ?? 0
  }));
  const gradeColor: Record<string, string> = { A: '#10b981', B: '#0ea5e9', C: '#f59e0b', D: '#fb7185', F: '#e11d48' };

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Score distribution" subtitle="How secure are scanned domains?" accent="amber">
          <ScoreHistogram s={s} />
        </Card>
        <Card title="Security posture" subtitle="Executive verdict mix" accent="green">
          <PostureDonut s={s} />
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Average score by module" subtitle="Mean 0–10 across all scans" accent="blue">
          {moduleData.length
            ? <HBars
                data={moduleData.map(m => ({ label: m.label, value: m.value, color: scoreColor(m.value), sub: '/10' }))}
                max={10}
                valueFormat={(v) => v.toFixed(1)}
              />
            : <ChartEmpty />}
          <p className="mt-3 text-[11px] text-ink-3">Lower bars flag the weakest control areas across the population.</p>
        </Card>

        <Card title="Email authentication grades" subtitle="SPF / DKIM / DMARC posture" accent="slate">
          {gradeData.some(g => g.value > 0)
            ? <div className="flex items-end justify-between gap-3 pt-2">
                {gradeData.map(g => {
                  const max = Math.max(1, ...gradeData.map(x => x.value));
                  return (
                    <div key={g.label} className="flex flex-1 flex-col items-center gap-2">
                      <span className="tnum text-xs font-semibold text-ink-3">{g.value}</span>
                      <div className="flex h-28 w-full items-end">
                        <div className="w-full rounded-t-md transition-all" style={{ height: `${(g.value / max) * 100}%`, background: gradeColor[g.label], minHeight: g.value ? 4 : 0 }} title={`Grade ${g.label}: ${g.value}`} />
                      </div>
                      <span className="text-sm font-bold" style={{ color: gradeColor[g.label] }}>{g.label}</span>
                    </div>
                  );
                })}
              </div>
            : <ChartEmpty />}
        </Card>
      </div>

      <Card title="Alert volume by severity" subtitle="Total findings surfaced across all scans" accent="rose">
        <SeverityBar counts={s.severity} />
      </Card>
    </>
  );
}

// ===========================================================================
// Section: Traffic
// ===========================================================================
function TrafficView({ s }: { s: AdminStats }) {
  const last30 = useMemo(() => fillDaily(s.daily), [s.daily]);
  const repeatTotal = s.repeat.once + s.repeat.repeat;

  return (
    <>
      <Card title="Daily scan volume" subtitle="Last 30 days" accent="blue">
        <Columns
          data={last30.map(d => ({ key: d.d, value: d.c, tip: `${d.d}: ${d.c} scan${d.c === 1 ? '' : 's'}` }))}
          label="Daily scan volume"
        />
        <DailyFooter days={last30} />
      </Card>

      <Card title="Activity heatmap" subtitle="Scans by hour of day × day of week (UTC)" accent="blue">
        <Heatmap data={s.hourly} />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Unique vs repeat domains" subtitle="One-time vs re-scanned targets" accent="green">
          {repeatTotal > 0
            ? <Donut
                data={[
                  { label: 'scanned once', value: s.repeat.once, color: '#0ea5e9' },
                  { label: 'repeat',       value: s.repeat.repeat, color: '#10b981' }
                ]}
                centerValue={repeatTotal}
                centerLabel="domains"
              />
            : <ChartEmpty />}
        </Card>

        <Card title="Scans by country" subtitle="Cloudflare-attributed origin (no IP stored)" accent="slate">
          <HBars
            data={s.by_country.map(c => ({ label: c.country, value: c.c }))}
            barClass="bg-emerald-500"
            mono
          />
        </Card>
      </div>

      <Card title="Top domains" subtitle="Most-scanned targets, all-time" accent="amber">
        <HBars
          data={s.top_domains.map(d => ({
            label: d.domain,
            value: d.c,
            color: undefined,
            sub: d.avg != null ? `avg ${d.avg.toFixed(1)}` : undefined
          }))}
          barClass="bg-sentinel-500"
          mono
        />
      </Card>
    </>
  );
}

// ===========================================================================
// Section: Explorer
// ===========================================================================
const PAGE = 50;
function ExplorerView() {
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [status, setStatus] = useState<'' | 'ok' | 'error'>('');
  const [min, setMin] = useState<string>('');
  const [max, setMax] = useState<string>('');
  const [sort, setSort] = useState<NonNullable<ScanQuery['sort']>>('ts');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);

  const query: ScanQuery = {
    q, country, status,
    min: min === '' ? '' : Number(min),
    max: max === '' ? '' : Number(max),
    sort, dir, limit: PAGE, offset: page * PAGE
  };

  const scans = useQuery({
    queryKey: ['admin-scans', query],
    queryFn: () => getAdminScans(query),
    placeholderData: keepPreviousData
  });

  const total = scans.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  const toggleSort = (key: NonNullable<ScanQuery['sort']>) => {
    if (sort === key) setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setDir('desc'); }
    setPage(0);
  };

  const exportCsv = async () => {
    const all = await getAdminScans({ ...query, limit: 1000, offset: 0 });
    downloadCsv(all.scans);
  };

  const reset = () => { setQ(''); setCountry(''); setStatus(''); setMin(''); setMax(''); setPage(0); };

  return (
    <Card title="Scan explorer" subtitle={`${total.toLocaleString()} matching scan${total === 1 ? '' : 's'}`} accent="blue"
      right={
        <button onClick={exportCsv} disabled={!total}
          className="inline-flex items-center gap-1.5 rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-xs font-semibold text-ink-2 hover:bg-panel-2 disabled:opacity-50">
          <Download className="h-3.5 w-3.5" aria-hidden="true" /> CSV
        </button>
      }
    >
      {/* Filters */}
      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
        <label className="relative lg:col-span-2">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" aria-hidden="true" />
          <input
            value={q} onChange={e => { setQ(e.target.value); setPage(0); }}
            placeholder="Search domain…"
            className="w-full rounded-md border border-line-2 bg-panel py-2 pl-8 pr-2 text-sm placeholder:text-ink-3 focus:border-sentinel-400 focus:outline-hidden focus:ring-2 focus:ring-sentinel-100"
          />
        </label>
        <input
          value={country} onChange={e => { setCountry(e.target.value.toUpperCase()); setPage(0); }}
          placeholder="Country (US)" maxLength={2}
          className="rounded-md border border-line-2 bg-panel px-2.5 py-2 text-sm uppercase placeholder:normal-case placeholder:text-ink-3 focus:border-sentinel-400 focus:outline-hidden focus:ring-2 focus:ring-sentinel-100"
        />
        <select
          value={status} onChange={e => { setStatus(e.target.value as '' | 'ok' | 'error'); setPage(0); }}
          className="rounded-md border border-line-2 bg-panel px-2.5 py-2 text-sm text-ink-2 focus:border-sentinel-400 focus:outline-hidden focus:ring-2 focus:ring-sentinel-100"
        >
          <option value="">All status</option>
          <option value="ok">Success</option>
          <option value="error">Error</option>
        </select>
        <input
          value={min} onChange={e => { setMin(e.target.value); setPage(0); }}
          placeholder="Min score" type="number" min={0} max={10} step={0.5}
          className="rounded-md border border-line-2 bg-panel px-2.5 py-2 text-sm placeholder:text-ink-3 focus:border-sentinel-400 focus:outline-hidden focus:ring-2 focus:ring-sentinel-100"
        />
        <input
          value={max} onChange={e => { setMax(e.target.value); setPage(0); }}
          placeholder="Max score" type="number" min={0} max={10} step={0.5}
          className="rounded-md border border-line-2 bg-panel px-2.5 py-2 text-sm placeholder:text-ink-3 focus:border-sentinel-400 focus:outline-hidden focus:ring-2 focus:ring-sentinel-100"
        />
      </div>

      {scans.isError && <Banner tone="bad" title={(scans.error as Error)?.message ?? 'Failed to load scans.'} />}

      {scans.data && total === 0 && !scans.isError && (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <Banner tone="info" title="No scans match these filters." />
          <button onClick={reset} className="text-xs font-semibold text-accent hover:underline">Clear filters</button>
        </div>
      )}

      {total > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border border-line">
            <table className="w-full text-sm">
              <thead className="bg-panel-2 text-[10px] uppercase tracking-wider text-ink-3">
                <tr>
                  <Th label="When" sortKey="ts" sort={sort} dir={dir} onSort={toggleSort} />
                  <Th label="Domain" sortKey="domain" sort={sort} dir={dir} onSort={toggleSort} />
                  <th className="p-3 text-left font-bold">Country</th>
                  <th className="p-3 text-left font-bold">Posture</th>
                  <th className="p-3 text-center font-bold">Issues</th>
                  <Th label="Time" sortKey="duration" sort={sort} dir={dir} onSort={toggleSort} className="text-right" />
                  <Th label="Score" sortKey="score" sort={sort} dir={dir} onSort={toggleSort} className="text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line bg-panel">
                {scans.data?.scans.map((r, i) => <ScanRow key={i} r={r} />)}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-ink-3">
            <span className="tnum">Page {page + 1} of {pages} · {total.toLocaleString()} rows</span>
            <div className="flex gap-1.5">
              <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}
                className="rounded-md border border-line-2 bg-panel px-3 py-1.5 font-medium hover:bg-panel-2 disabled:opacity-40">Prev</button>
              <button disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)}
                className="rounded-md border border-line-2 bg-panel px-3 py-1.5 font-medium hover:bg-panel-2 disabled:opacity-40">Next</button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

function Th({ label, sortKey, sort, dir, onSort, className = '' }: {
  label: string; sortKey: NonNullable<ScanQuery['sort']>;
  sort: string; dir: string; onSort: (k: NonNullable<ScanQuery['sort']>) => void; className?: string;
}) {
  const active = sort === sortKey;
  return (
    <th className={`p-3 font-bold ${className || 'text-left'}`}>
      <button onClick={() => onSort(sortKey)} className={`inline-flex items-center gap-1 hover:text-ink ${active ? 'text-accent' : ''}`}>
        {label}
        <ArrowUpDown className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-30'}`} aria-hidden="true" />
        {active && <span className="sr-only">{dir === 'asc' ? 'ascending' : 'descending'}</span>}
      </button>
    </th>
  );
}

function ScanRow({ r }: { r: AdminScan }) {
  const issues = (r.alerts_critical ?? 0) + (r.alerts_high ?? 0);
  const err = r.status === 'error';
  return (
    <tr className="hover:bg-panel-2">
      <td className="whitespace-nowrap p-3 text-xs text-ink-3">{relativeTime(r.ts)}</td>
      <td className="p-3 font-mono text-sm text-ink">{r.domain}</td>
      <td className="p-3 font-mono text-xs text-ink-3">{r.country ?? '—'}</td>
      <td className="p-3">
        {err
          ? <span className="pill pill-bad">error</span>
          : r.posture
            ? <span className="pill capitalize" style={{ background: `${POSTURE_COLOR[r.posture]}1a`, color: POSTURE_COLOR[r.posture] }}>{r.posture}</span>
            : <span className="text-ink-3">—</span>}
      </td>
      <td className="p-3 text-center">
        {err ? <span className="text-ink-3">—</span>
          : issues > 0
            ? <span className="tnum inline-flex items-center gap-1 text-xs font-semibold text-rose-600"><AlertTriangle className="h-3 w-3" aria-hidden="true" />{issues}</span>
            : <span className="text-xs text-emerald-600">clean</span>}
      </td>
      <td className="tnum p-3 text-right text-xs text-ink-3">{r.duration_ms != null ? fmtDuration(r.duration_ms) : '—'}</td>
      <td className="p-3 text-right">
        {r.score == null ? <span className="text-ink-3">—</span>
          : <span className={`tnum font-semibold ${scoreTextClass(r.score)}`}>{r.score.toFixed(1)}</span>}
      </td>
    </tr>
  );
}

// ===========================================================================
// Section: Health
// ===========================================================================
function HealthView({ s }: { s?: AdminStats }) {
  const health = useQuery({ queryKey: ['admin-health'], queryFn: getHealth, refetchInterval: 30_000, retry: 1 });
  const feed = useQuery({
    queryKey: ['admin-feed'],
    queryFn: () => getAdminScans({ limit: 15, sort: 'ts', dir: 'desc' }),
    refetchInterval: 15_000
  });

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="API status"
          value={health.isLoading ? '…' : health.data?.ok ? 'Operational' : 'Down'}
          tone={health.data?.ok ? 'ok' : health.isError ? 'bad' : 'neutral'}
          hint={health.isError ? 'unreachable' : 'health endpoint'}
          icon={<Server className="h-4 w-4" />}
        />
        <StatCard label="Version" value={health.data?.version ?? '—'} tone="neutral" hint="deployed worker" icon={<Layers className="h-4 w-4" />} />
        <StatCard label="Error rate" value={s ? `${(s.summary.error_rate * 100).toFixed(1)}%` : '—'} tone={!s ? 'neutral' : s.summary.error_rate > 0.05 ? 'bad' : s.summary.error_rate > 0 ? 'warn' : 'ok'} hint={s ? `${s.summary.errors} failed / ${s.summary.total}` : ''} icon={<AlertTriangle className="h-4 w-4" />} />
        <StatCard label="Avg scan time" value={s?.summary.avg_duration_ms != null ? fmtDuration(s.summary.avg_duration_ms) : '—'} tone="neutral" hint="wall-clock" icon={<Clock className="h-4 w-4" />} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card title="Live scan feed" subtitle="Auto-refreshing every 15s" accent="blue"
            right={<span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600"><span className="ok-dot" aria-hidden="true" /> live</span>}
          >
            {feed.isError && <Banner tone="bad" title={(feed.error as Error)?.message ?? 'Feed unavailable.'} />}
            {feed.data && feed.data.scans.length === 0 && <ChartEmpty label="No scans yet" />}
            {feed.data && feed.data.scans.length > 0 && (
              <ul className="divide-y divide-line">
                {feed.data.scans.map((r, i) => (
                  <li key={i} className="flex items-center gap-3 py-2.5">
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${r.status === 'error' ? 'bg-rose-500' : 'bg-emerald-500'}`} aria-hidden="true" />
                    <span className="truncate font-mono text-sm text-ink">{r.domain}</span>
                    <span className="ml-auto shrink-0 font-mono text-[11px] text-ink-3">{r.country ?? '—'}</span>
                    <span className="w-16 shrink-0 text-right text-[11px] text-ink-3">{relativeTime(r.ts)}</span>
                    <span className="w-9 shrink-0 text-right">
                      {r.status === 'error'
                        ? <span className="text-xs font-semibold text-rose-500">err</span>
                        : r.score != null
                          ? <span className={`tnum text-sm font-semibold ${scoreTextClass(r.score)}`}>{r.score.toFixed(1)}</span>
                          : <span className="text-ink-3">—</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Authentication" accent="green">
            <dl className="space-y-3 text-sm">
              <Row k="Method" v="Cloudflare Zero Trust" />
              <Row k="Gate" v="Access (edge-enforced)" />
              <Row k="Identity" v={<span className="font-mono text-xs">{s?.admin_email ?? '—'}</span>} />
              <Row k="Logout" v={<a className="text-accent hover:underline" href="/cdn-cgi/access/logout">/cdn-cgi/access/logout</a>} />
            </dl>
          </Card>
          <Card title="Privacy posture" accent="slate">
            <ul className="space-y-2 text-xs text-ink-2">
              <li className="flex gap-2"><span className="text-emerald-500">✓</span> Aggregate-only logging</li>
              <li className="flex gap-2"><span className="text-emerald-500">✓</span> No IPs, user-agents, or result bodies</li>
              <li className="flex gap-2"><span className="text-emerald-500">✓</span> Public scans remain ephemeral</li>
            </ul>
          </Card>
        </div>
      </div>

      {/* Reliability — WHY scans fail and which analyzers drop out. */}
      {s && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Errors by category" subtitle="Why recent scans failed" accent="rose">
            {(s.errors_by_category?.length ?? 0) > 0
              ? <HBars data={(s.errors_by_category ?? []).map(e => ({ label: e.category, value: e.c, color: '#fb7185' }))} />
              : <ChartEmpty label="No failed scans recorded" />}
          </Card>
          <Card title="Module dropout" subtitle="Analyzers that returned no data (transient upstream)" accent="amber">
            {s.module_dropouts && s.module_dropouts.ok_total > 0
              ? <HBars
                  max={s.module_dropouts.ok_total}
                  data={[
                    { label: 'DNS', value: s.module_dropouts.dns },
                    { label: 'Email', value: s.module_dropouts.email },
                    { label: 'Web', value: s.module_dropouts.web },
                    { label: 'SSL', value: s.module_dropouts.ssl },
                    { label: 'WHOIS', value: s.module_dropouts.whois }
                  ].map(m => ({ label: m.label, value: m.value, color: '#f59e0b', sub: `/ ${s.module_dropouts!.ok_total}` }))}
                />
              : <ChartEmpty label="No dropouts recorded" />}
            <p className="mt-3 text-[11px] text-ink-3">
              A module drops out when its upstream (CT logs, RDAP, DoH) is transiently unreachable; the scan still scores on the rest.
              {s.source_health && s.source_health.whois_rate_limited > 0 &&
                ` RDAP rate-limited on ${s.source_health.whois_rate_limited} scan${s.source_health.whois_rate_limited === 1 ? '' : 's'}.`}
            </p>
          </Card>
        </div>
      )}
    </>
  );
}

// ===========================================================================
// Shared sub-components
// ===========================================================================
function Card({ title, subtitle, accent = 'blue', right, children }: {
  title: string; subtitle?: string;
  accent?: 'blue' | 'green' | 'amber' | 'rose' | 'slate';
  right?: React.ReactNode; children: React.ReactNode;
}) {
  const cls = {
    blue: 'panel-accent-blue', green: 'panel-accent-green', amber: 'panel-accent-amber',
    rose: 'panel-accent-rose', slate: 'panel-accent-slate'
  }[accent];
  return (
    <section className={`${cls} p-4 sm:p-5`}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          {subtitle && <p className="mt-0.5 text-[11px] text-ink-3 sm:text-xs">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-[11px] font-bold uppercase tracking-wider text-ink-3">{k}</dt>
      <dd className="truncate text-right text-sm font-medium text-ink">{v}</dd>
    </div>
  );
}

function PostureDonut({ s }: { s: AdminStats }) {
  const total = s.posture_mix.reduce((a, b) => a + b.c, 0);
  if (total === 0) return <ChartEmpty />;
  const data = POSTURE_ORDER
    .map(p => ({ label: p, value: s.posture_mix.find(x => x.posture === p)?.c ?? 0, color: POSTURE_COLOR[p] }))
    .filter(d => d.value > 0);
  return <Donut data={data} centerValue={total} centerLabel="scans" />;
}

function ScoreHistogram({ s }: { s: AdminStats }) {
  const buckets = Array.from({ length: 10 }, (_, i) => ({
    key: `${i}`,
    value: s.score_dist.find(x => x.bucket === i)?.c ?? 0,
    color: scoreColor(i + 0.5),
    tip: `Score ${i}–${i + 1}: ${s.score_dist.find(x => x.bucket === i)?.c ?? 0} scans`
  }));
  const has = buckets.some(b => b.value > 0);
  if (!has) return <ChartEmpty />;
  return (
    <>
      <Columns data={buckets} label="Score distribution" valueFormat={(v) => `${v}`} />
      <div className="mt-1 flex justify-between px-1 text-[10px] text-ink-3">
        <span>0 — critical</span><span>5</span><span>10 — excellent</span>
      </div>
    </>
  );
}

function DailyFooter({ days }: { days: { d: string; c: number }[] }) {
  const peak = Math.max(0, ...days.map(d => d.c));
  const sum = days.reduce((a, b) => a + b.c, 0);
  return (
    <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 text-[10px] text-ink-3">
      <span className="font-mono">{days[0]?.d}</span>
      <span className="font-medium text-ink-3">{sum} total · peak {peak}</span>
      <span className="font-mono">{days[days.length - 1]?.d}</span>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="panel h-24 animate-pulse" />)}
      </div>
      <div className="panel h-56 animate-pulse" />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="panel h-64 animate-pulse" />
        <div className="panel h-64 animate-pulse" />
      </div>
    </div>
  );
}

// ===========================================================================
// helpers
// ===========================================================================
function delta(cur: number, prev: number): number | null {
  if (prev <= 0) return null;
  return ((cur - prev) / prev) * 100;
}

function scoreTone(score: number | null): 'ok' | 'warn' | 'bad' | 'neutral' {
  if (score == null) return 'neutral';
  if (score >= 8) return 'ok';
  if (score >= 5) return 'warn';
  return 'bad';
}

/** Pad sparse daily rows to a continuous 30-day series ending today. */
function fillDaily(daily: { d: string; c: number; avg?: number | null }[]): { d: string; c: number }[] {
  const out: { d: string; c: number }[] = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const dt = new Date(today);
    dt.setUTCDate(today.getUTCDate() - i);
    const key = dt.toISOString().slice(0, 10);
    out.push({ d: key, c: daily.find(x => x.d === key)?.c ?? 0 });
  }
  return out;
}

function fmtDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ms).toLocaleDateString();
}

function downloadCsv(rows: AdminScan[]) {
  const cols: (keyof AdminScan)[] = [
    'ts', 'domain', 'country', 'status', 'score', 'posture', 'email_grade',
    'duration_ms', 'alerts_critical', 'alerts_high', 'alerts_medium', 'alerts_low',
    'exposure_exposed', 'vuln_count'
  ];
  const head = ['datetime', ...cols.filter(c => c !== 'ts')].join(',');
  const body = rows.map(r =>
    [new Date(r.ts).toISOString(), ...cols.filter(c => c !== 'ts').map(c => {
      const v = r[c];
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    })].join(',')
  ).join('\n');
  const blob = new Blob([`${head}\n${body}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `protbyte-sentinel-scans-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
